/* eslint-disable react-compiler/react-compiler */
import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

import { useListDispatchersAutocompleteQuery } from '@/common/infrastructure/gql/generated/schema';

/**
 * Type representing a dispatcher from the GraphQL query result
 */
type Dispatcher = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useListDispatchersAutocompleteQuery>['data']>['dispatchers']>['items']
>[number];

export interface UseListDispatchersAutocompleteParams {
  searchTerm?: string;
  debounceMs?: number;
  onDispatchersSelect?: (dispatchers: AutocompleteOption) => void;
  skipQuery?: boolean;
}

/**
 * Hook for implementing dispatcher autocomplete functionality
 *
 * @param params - Configuration object
 * @param params.searchTerm - The current search term to filter dispatchers by (default: '')
 * @param params.debounceMs - Debounce time in milliseconds for search term updates (default: 1000ms)
 * @param params.onDispatchersSelect - Optional callback when a dispatcher is selected
 * @param params.skipQuery - When true, the query is not executed (default: false)
 * @returns Object containing dispatcher options, loading state, error state, and filter function
 */
export const useListDispatchersAutocomplete = ({
  searchTerm = '',
  debounceMs = 1000,
  onDispatchersSelect,
  skipQuery = false
}: UseListDispatchersAutocompleteParams = {}) => {
  return useAutocomplete<Dispatcher>({
    searchTerm,
    debounceMs,
    onSelect: onDispatchersSelect,
    query: (search?: string) => {
      const { data, loading, error } = useListDispatchersAutocompleteQuery({
        variables: { skip: 0, search: search ?? searchTerm },
        fetchPolicy: 'cache-first',
        skip: skipQuery
      });
      return {
        data: { items: data?.dispatchers?.items },
        loading: loading,
        error: error ?? null
      };
    },
    transform: (dispatchers) => ({
      value: dispatchers.id ?? -1,
      label: dispatchers.name ?? 'Unknown Dispatcher',
      userId: (dispatchers.user as { userId?: number | null })?.userId ?? null
    })
  });
};
